import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Setup.css';
import './Button.css';
import PopulateSharksModal from './PopulateSharksModal';
import AdminPasswordModal from './AdminPasswordModal';
import SimulateRoundModal from './SimulateRoundModal';
import EditCatchModal from './EditCatchModal';
import EventInfoModal from './EventInfoModal';
import SponsorUploadModal from './SponsorUploadModal';

function Setup() {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [calcuttaWinners, setCalcuttaWinners] = useState(1);
  const [showCalcutta, setShowCalcutta] = useState(true);
  const [currentRound, setCurrentRound] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isSimulateModalOpen, setIsSimulateModalOpen] = useState(false);
  const [isEditCatchModalOpen, setIsEditCatchModalOpen] = useState(false);
  const [isEventInfoModalOpen, setIsEventInfoModalOpen] = useState(false);
  const [isSponsorModalOpen, setIsSponsorModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);

  useEffect(() => {
    const fetchSetupData = async () => {
      try {
        const [resultsRes, roundRes] = await Promise.all([
          api.get('/results'),
          api.get('/tournament/round'),
        ]);
        setCalcuttaWinners(resultsRes.data.numCalcuttaWinners);
        setShowCalcutta(resultsRes.data.showCalcutta);
        setCurrentRound(roundRes.data.current_round);
      } catch (error) {
        console.error('Error fetching setup data:', error);
      }
    };
    fetchSetupData();
  }, []);

  const handleAdminAction = (action, confirmation = null) => {
    if (confirmation && !window.confirm(confirmation)) {
      return;
    }
    setCurrentAction(action);
    setIsPasswordModalOpen(true);
  };

  const executeAdminAction = async (action, password, params = {}) => {
    const formData = new FormData();
    formData.append('password', password);

    setIsExecuting(true);
    setMessage('');

    try {
      const url = action.startsWith('tournament/')
        ? `/${action}`
        : `/admin/${action}`;

      const queryParams = new URLSearchParams(params).toString();
      const fullUrl = queryParams ? `${url}?${queryParams}` : url;

      const response = await api.post(fullUrl, formData);
      setMessage(response.data.message || response.data.detail);
      setMessageType('success');

      if (action === 'new-tournament') {
        setIsModalOpen(true);
      }

      if (action === 'settings/show-calcutta') {
        setShowCalcutta(params.show);
      } else if (action === 'settings/calcutta-winners') {
        setCalcuttaWinners(params.winners);
      } else if (action === 'tournament/advance-round') {
        try {
          const roundRes = await api.get('/tournament/round');
          setCurrentRound(roundRes.data.current_round);
        } catch (e) {}
      }
    } catch (error) {
      const errorData = error.response ? error.response.data : { detail: 'An unexpected error occurred.' };
      setMessage(errorData.detail || 'An error occurred.');
      setMessageType('error');
    } finally {
      setIsExecuting(false);
    }
  };

  const handleCalcuttaWinnersChange = () => {
    const winners = window.prompt('Enter the number of Calcutta winners (0-3):');
    if (winners === null) return;
    const num = parseInt(winners, 10);
    if (isNaN(num) || num < 0 || num > 3) {
      alert('Invalid input. Please enter a number between 0 and 3.');
      return;
    }
    handleAdminAction({ action: 'settings/calcutta-winners', params: { winners: num } });
  };

  const handleToggleCalcutta = () => {
    handleAdminAction({
      action: 'settings/show-calcutta',
      params: { show: !showCalcutta },
    });
  };

  const handleResetCatches = async () => {
    const choice = window.prompt("Type 'CURRENT' to reset only the current round's catches, or 'ALL' to reset the entire tournament.");
    if (choice === 'CURRENT') {
      const roundResponse = await api.get('/tournament/round');
      const currentRound = roundResponse.data.current_round;
      handleAdminAction({ action: 'reset-catches', params: { round_number: currentRound } });
    } else if (choice === 'ALL') {
      handleAdminAction('reset-catches');
    }
  };

  return (
    <>
      <main className="setup-main">

        {/* ── Event & Sponsor Info ── */}
        <section className="setup-section">
          <h2>Event Information</h2>
          <div className="button-container">
            <button className="btn btn-primary" onClick={() => setIsEventInfoModalOpen(true)}>Update Event Info</button>
            <button className="btn btn-primary" onClick={() => setIsSponsorModalOpen(true)}>Update Sponsors</button>
          </div>
        </section>

        {/* ── Shark Management ── */}
        <section className="setup-section">
          <h2>Shark Information</h2>
          <div className="button-container">
            <button className="btn btn-primary" onClick={() => navigate('/shark-list')}>Shark List</button>
            <button className="btn btn-primary" onClick={() => navigate('/add-shark')}>Add Shark</button>
            <button className="btn btn-primary" onClick={() => navigate('/edit-shark')}>Edit Shark</button>
          </div>
        </section>

        {/* ── Tournament Setup ── */}
        <section className="setup-section">
          <h2>
            Tournament Setup
            {currentRound && <span className="round-badge">Round {currentRound}</span>}
          </h2>

          <div className="stat-rows">
            <div className="stat-row">
              <span className="stat-label">
                Calcutta Winners: <span className="stat-value">{calcuttaWinners}</span>
              </span>
              <button className="btn btn-primary btn-sm" onClick={handleCalcuttaWinnersChange}>
                Change
              </button>
            </div>
            <div className="stat-row">
              <span className="stat-label">
                Show Calcutta on Results: <span className="stat-value">{showCalcutta ? 'Yes' : 'No'}</span>
              </span>
              <button
                className={`btn btn-sm ${showCalcutta ? 'btn-danger' : 'btn-success'}`}
                onClick={handleToggleCalcutta}
              >
                {showCalcutta ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>

          <hr className="section-divider" />

          <div className="button-container">
            <button className="btn btn-primary" onClick={() => handleAdminAction('tournament/advance-round')}>Advance Round</button>
            <button className="btn btn-secondary" onClick={() => handleAdminAction('add-extra-bait')}>Add Extra Bait for Everyone</button>
            <button className="btn btn-secondary" onClick={() => handleAdminAction('add-calcutta')}>Add Calcutta for Everyone</button>
            <button className="btn btn-success" onClick={() => handleAdminAction('new-tournament')}>New Tournament</button>
          </div>
        </section>

        {/* ── Admin Actions ── */}
        <section className="setup-section">
          <h2>Admin Actions</h2>

          <div className="button-container">
            <button className="btn btn-secondary" onClick={() => setIsModalOpen(true)}>Populate Sharks</button>
            <button className="btn btn-secondary" onClick={() => handleAdminAction('populate-anglers')}>Populate Anglers</button>
            <button className="btn btn-secondary" onClick={() => setIsSimulateModalOpen(true)}>Simulate Round</button>
            <button className="btn btn-info" onClick={() => setIsEditCatchModalOpen(true)}>Edit Catch Data</button>
            <button className="btn btn-info" onClick={() => navigate('/audit-log')}>View Audit Log</button>
          </div>

          <div className="danger-zone">
            <p className="danger-zone-label">⚠ Danger Zone — these actions cannot be undone</p>
            <div className="button-container">
              <button className="btn btn-danger" onClick={() => handleAdminAction('purge-anglers', 'Are you sure you want to DELETE ALL fishermen? This cannot be undone.')}>Purge Anglers</button>
              <button className="btn btn-danger" onClick={handleResetCatches}>Reset Catches</button>
              <button className="btn btn-danger" onClick={() => handleAdminAction('reset-tournament', 'Are you sure you want to DELETE ALL tournament data (anglers, fish, and catches)? This cannot be undone.')}>Start with Blank Database</button>
              <button className="btn btn-danger" onClick={() => handleAdminAction('restart', 'Are you sure you want to restart the server? All active clients will be briefly disconnected.')}>Restart Server</button>
              <button className="btn btn-danger" onClick={() => handleAdminAction('end-tournament', 'Are you sure you want to end the tournament? This will make the database read-only.')}>End Tournament</button>
            </div>
          </div>

          {isExecuting && <p className="setup-message info">Executing action…</p>}
          {message && <p className={`setup-message ${messageType}`}>{message}</p>}
        </section>

      </main>

      {isModalOpen && <PopulateSharksModal onClose={() => setIsModalOpen(false)} setBannerMessage={setMessage} />}
      {isPasswordModalOpen && (
        <AdminPasswordModal
          onConfirm={(password) => {
            setIsPasswordModalOpen(false);
            if (currentAction) {
              if (typeof currentAction === 'string') {
                executeAdminAction(currentAction, password);
              } else {
                executeAdminAction(currentAction.action, password, currentAction.params);
              }
            }
          }}
          onCancel={() => setIsPasswordModalOpen(false)}
        />
      )}
      {isSimulateModalOpen && (
        <SimulateRoundModal
          onConfirm={(password, percentage) => {
            setIsSimulateModalOpen(false);
            executeAdminAction('simulate-round', password, { percentage });
          }}
          onCancel={() => setIsSimulateModalOpen(false)}
        />
      )}
      {isEditCatchModalOpen && (
        <EditCatchModal
          onClose={() => setIsEditCatchModalOpen(false)}
          onSuccess={() => { setMessage('Catch updated successfully.'); setMessageType('success'); }}
        />
      )}
      {isEventInfoModalOpen && (
        <EventInfoModal
          onClose={() => setIsEventInfoModalOpen(false)}
          onSuccess={() => { setMessage('Event info updated successfully.'); setMessageType('success'); }}
        />
      )}
      {isSponsorModalOpen && (
        <SponsorUploadModal
          onClose={() => setIsSponsorModalOpen(false)}
          onSuccess={() => { setMessage('Sponsor logos updated successfully.'); setMessageType('success'); }}
        />
      )}
    </>
  );
}

export default Setup;
